import React, { useState, useEffect } from 'react';
import { 
  Users, 
  UserPlus, 
  UserCheck, 
  UserX, 
  Shield, 
  Mail, 
  Clock,
  AlertCircle,
  CheckCircle,
  Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import api from '../lib/api';

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'auditor' | 'user';
  isActive: boolean;
  emailVerified: boolean;
  mfaEnabled: boolean;
  duoEnabled?: boolean;
  approvalStatus: 'pending' | 'approved' | 'rejected';
  approvedBy?: number;
  approvedAt?: string;
  rejectionReason?: string;
  lastLogin?: string;
  createdAt: string;
}

export function UserManagementPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | 'pending'>('all');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; user: User | null }>({ show: false, user: null });
  const { toast } = useToast();

  // Load users and pending users
  useEffect(() => {
    loadUsers();
    loadPendingUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/auth/admin/users') as { users: User[]; total: number };
      setUsers(response.users || []);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast({
        title: "Error",
        description: "Failed to load users",
        variant: "destructive",
      });
    }
  };

  const loadPendingUsers = async () => {
    try {
      const response = await api.get('/auth/admin/users/pending') as { users: User[] };
      setPendingUsers(response.users || []);
    } catch (error) {
      console.error('Failed to load pending users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const approveUser = async (userId: number) => {
    try {
      await api.post(`/auth/admin/users/${userId}/approve`);
      toast({
        title: "User Approved",
        description: "User has been approved and will receive an email to set their password.",
      });
      loadUsers();
      loadPendingUsers();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to approve user",
        variant: "destructive",
      });
    }
  };

  const rejectUser = async (userId: number, reason: string) => {
    try {
      await api.post(`/auth/admin/users/${userId}/reject`, { reason });
      toast({
        title: "User Rejected",
        description: "User has been rejected and will receive a notification email.",
      });
      loadUsers();
      loadPendingUsers();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to reject user",
        variant: "destructive",
      });
    }
  };

  const updateUserRole = async (userId: number, newRole: string) => {
    try {
      await api.put(`/auth/admin/users/${userId}/role`, { role: newRole });
      toast({
        title: "Role Updated",
        description: "User role has been updated successfully.",
      });
      loadUsers();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to update user role",
        variant: "destructive",
      });
    }
  };

  const sendPasswordReset = async (userId: number) => {
    try {
      await api.post(`/auth/admin/users/${userId}/reset-password`);
      toast({
        title: "Password Reset Sent",
        description: "Password reset email has been sent to the user.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to send password reset",
        variant: "destructive",
      });
    }
  };

  const deleteUser = async (userId: number) => {
    try {
      await api.deleteUser(userId);
      toast({
        title: "User Deleted",
        description: "User has been permanently deleted from the system.",
      });
      // Refresh user lists
      loadUsers();
      loadPendingUsers();
      setDeleteConfirm({ show: false, user: null });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to delete user",
        variant: "destructive",
      });
    }
  };

  const enableMFA = async (userId: number) => {
    try {
      await api.enableUserMFA(userId);
      toast({
        title: "MFA Enabled",
        description: "User will be required to set up MFA on their next login.",
      });
      loadUsers();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to enable MFA",
        variant: "destructive",
      });
    }
  };

  const disableMFA = async (userId: number) => {
    try {
      await api.disableUserMFA(userId);
      toast({
        title: "MFA Disabled",
        description: "MFA has been disabled for this user.",
      });
      loadUsers();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to disable MFA",
        variant: "destructive",
      });
    }
  };

  const enableDUO = async (userId: number, currentEmail: string) => {
    try {
      const duoUsername = prompt('DUO username (leave blank to use user\'s email):', currentEmail) || undefined;
      await api.enableUserDuo(userId, duoUsername);
      toast({
        title: "DUO Enabled",
        description: "Duo Push has been enabled for this user.",
      });
      loadUsers();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to enable DUO",
        variant: "destructive",
      });
    }
  };

  const disableDUO = async (userId: number) => {
    try {
      await api.disableUserDuo(userId);
      toast({
        title: "DUO Disabled",
        description: "Duo Push has been disabled for this user.",
      });
      loadUsers();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to disable DUO",
        variant: "destructive",
      });
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin': 
        return <Badge className="bg-red-100 text-red-800 border-red-200">{role}</Badge>;
      case 'auditor': 
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">{role}</Badge>;
      case 'user': 
        return <Badge className="bg-green-100 text-green-800 border-green-200">{role}</Badge>;
      default: 
        return <Badge variant="secondary">{role}</Badge>;
    }
  };

  const getStatusBadge = (user: User) => {
    const { approvalStatus, isActive } = user;
    
    // Combined status message
    let statusText, badgeClass;
    
    switch (approvalStatus) {
      case 'approved':
        if (isActive) {
          statusText = "Active";
          badgeClass = "bg-green-100 text-green-800 border-green-200";
        } else {
          statusText = "Inactive";
          badgeClass = "bg-gray-100 text-gray-800 border-gray-200";
        }
        break;
      case 'pending':
        statusText = "Pending Approval";
        badgeClass = "bg-yellow-100 text-yellow-800 border-yellow-200";
        break;
      case 'rejected':
        statusText = "Rejected";
        badgeClass = "bg-red-100 text-red-800 border-red-200";
        break;
      default:
        statusText = approvalStatus || "Unknown";
        badgeClass = "bg-gray-100 text-gray-800 border-gray-200";
    }

    return <Badge className={badgeClass}>{statusText}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">User Management</h1>
          <p className="text-gray-600 mt-1">Manage users, roles, and permissions</p>
        </div>
        <Button onClick={() => setShowInviteModal(true)} className="flex items-center gap-2">
          <UserPlus className="h-4 w-4" />
          Invite User
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="flex items-center p-6">
            <Users className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Total Users</p>
              <p className="text-2xl font-bold">{users.length}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="flex items-center p-6">
            <Clock className="h-8 w-8 text-yellow-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Pending Approval</p>
              <p className="text-2xl font-bold">{pendingUsers.length}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="flex items-center p-6">
            <Shield className="h-8 w-8 text-red-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Admins</p>
              <p className="text-2xl font-bold">{users.filter(u => u.role === 'admin').length}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="flex items-center p-6">
            <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Active Users</p>
              <p className="text-2xl font-bold">{users.filter(u => u.isActive).length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'all'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          All Users ({users.length})
        </button>
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'pending'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Pending Approval ({pendingUsers.length})
        </button>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            {activeTab === 'all' ? 'All Users' : 'Pending Approval'}
          </CardTitle>
          <CardDescription>
            {activeTab === 'all' 
              ? 'Manage existing users, roles, and permissions'
              : 'Review and approve new user registrations'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-4">User</th>
                  <th className="text-left p-4">Role</th>
                  <th className="text-left p-4">Status</th>
                  <th className="text-left p-4">Last Login</th>
                  <th className="text-left p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(activeTab === 'all' ? users : pendingUsers).map((user) => (
                  <tr key={user.id} className="border-b hover:bg-gray-50">
                    <td className="p-4">
                      <div>
                        <div className="font-medium">{user.firstName} {user.lastName}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </td>
                    <td className="p-4">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="p-4">
                      <div className="flex flex-col gap-1">
                        {getStatusBadge(user)}
                        {user.emailVerified ? (
                          <span className="text-xs text-green-600 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            Email verified
                          </span>
                        ) : (
                          <span className="text-xs text-yellow-600 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            Email not verified
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="text-sm">
                        {user.lastLogin 
                          ? new Date(user.lastLogin).toLocaleDateString()
                          : 'Never'
                        }
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        {user.approvalStatus === 'pending' ? (
                          <>
                            <Button
                              size="sm"
                              onClick={() => approveUser(user.id)}
                              className="flex items-center gap-1"
                            >
                              <UserCheck className="h-3 w-3" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                const reason = prompt('Reason for rejection (optional):');
                                if (reason !== null) {
                                  rejectUser(user.id, reason);
                                }
                              }}
                              className="flex items-center gap-1"
                            >
                              <UserX className="h-3 w-3" />
                              Reject
                            </Button>
                          </>
                        ) : (
                          <>
                            <select
                              value={user.role}
                              onChange={(e) => updateUserRole(user.id, e.target.value)}
                              className="text-xs border rounded px-2 py-1"
                            >
                              <option value="user">User</option>
                              <option value="auditor">Auditor</option>
                              <option value="admin">Admin</option>
                            </select>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => sendPasswordReset(user.id)}
                              className="flex items-center gap-1"
                            >
                              <Mail className="h-3 w-3" />
                              Reset Password
                            </Button>
                            {user.mfaEnabled ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => disableMFA(user.id)}
                                className="flex items-center gap-1 border-red-300 text-red-700 hover:bg-red-50"
                              >
                                <Shield className="h-3 w-3" />
                                Disable MFA
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => enableMFA(user.id)}
                                className="flex items-center gap-1 border-green-300 text-green-700 hover:bg-green-50"
                              >
                                <Shield className="h-3 w-3" />
                                Enable MFA
                              </Button>
                            )}
                            {user.duoEnabled ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => disableDUO(user.id)}
                                className="flex items-center gap-1 border-red-300 text-red-700 hover:bg-red-50"
                              >
                                <Shield className="h-3 w-3" />
                                Disable DUO
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => enableDUO(user.id, user.email)}
                                className="flex items-center gap-1 border-blue-300 text-blue-700 hover:bg-blue-50"
                              >
                                <Shield className="h-3 w-3" />
                                Enable DUO
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => setDeleteConfirm({ show: true, user })}
                              className="flex items-center gap-1"
                            >
                              <Trash2 className="h-3 w-3" />
                              Delete
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(activeTab === 'all' ? users : pendingUsers).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                {activeTab === 'all' ? 'No users found' : 'No pending approvals'}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Invite User Modal */}
      {showInviteModal && (
        <InviteUserModal
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            setShowInviteModal(false);
            loadUsers();
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm.show && deleteConfirm.user && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-red-600">Delete User</CardTitle>
              <CardDescription>
                Are you sure you want to permanently delete this user? This action cannot be undone.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="text-sm">
                    <p className="font-medium text-red-800">User to be deleted:</p>
                    <p className="text-red-700">
                      {deleteConfirm.user.firstName} {deleteConfirm.user.lastName} ({deleteConfirm.user.email})
                    </p>
                    <p className="text-red-700">Role: {deleteConfirm.user.role}</p>
                  </div>
                </div>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Warning:</strong> This will permanently remove the user and all associated data from the system.
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    onClick={() => deleteUser(deleteConfirm.user!.id)}
                    className="flex-1"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Permanently
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setDeleteConfirm({ show: false, user: null })}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// Invite User Modal Component
interface InviteUserModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

function InviteUserModal({ onClose, onSuccess }: InviteUserModalProps) {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'user'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await api.post('/auth/admin/users/invite', formData);
      toast({
        title: "Invitation Sent",
        description: "User invitation has been sent successfully.",
      });
      onSuccess();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to send invitation",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Invite New User</CardTitle>
          <CardDescription>
            Send an invitation email to a new user
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
                placeholder="user@example.com"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">First Name</label>
                <input
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full border rounded-md px-3 py-2"
                  placeholder="John"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Last Name</label>
                <input
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full border rounded-md px-3 py-2"
                  placeholder="Doe"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="user">User</option>
                <option value="auditor">Auditor</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            
            <div className="flex gap-2">
              <Button type="submit" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? "Sending..." : "Send Invitation"}
              </Button>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
